// XSS Protection utilities using DOMPurify
import DOMPurify from 'dompurify';

// Configure DOMPurify with strict settings
const sanitizeConfig = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p'], // Very limited HTML tags
  ALLOWED_ATTR: [], // No attributes allowed
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false
};

// Sanitize HTML content while preserving safe formatting
export function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  return DOMPurify.sanitize(html, sanitizeConfig);
}

// Sanitize plain text (remove all HTML)
export function sanitizeText(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // Remove all HTML tags and decode entities
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
}

// Validate and sanitize Instagram handle
export function sanitizeInstagramHandle(handle) {
  if (!handle || typeof handle !== 'string') {
    return '';
  }
  
  // Remove @ symbol and sanitize
  const clean = handle.replace(/^@/, '').trim();
  
  // Only allow alphanumeric characters, dots, and underscores
  const validHandle = clean.replace(/[^a-zA-Z0-9._]/g, '');
  
  // Limit length and return
  return validHandle.substring(0, 30);
}

// Validate URL for external links
export function validateURL(url) {
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  try {
    const urlObj = new URL(url);
    
    // Only allow https URLs to trusted domains
    if (urlObj.protocol !== 'https:') {
      return '';
    }
    
    // Whitelist of allowed domains
    const allowedDomains = [
      'instagram.com',
      'www.instagram.com',
      'google.com',
      'www.google.com',
      'maps.google.com'
    ];
    
    if (!allowedDomains.includes(urlObj.hostname.toLowerCase())) {
      return '';
    }
    
    return urlObj.toString();
  } catch (error) {
    return '';
  }
}

// Create safe Instagram URL
export function createInstagramURL(handle) {
  const safeHandle = sanitizeInstagramHandle(handle);
  if (!safeHandle) {
    return '';
  }
  
  return `https://instagram.com/${safeHandle}`;
}

// Create safe Google Maps URL
export function createMapsURL(lat, lng) {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);
  
  if (isNaN(latitude) || isNaN(longitude)) {
    return '';
  }
  
  // Validate coordinates are reasonable (not extreme values)
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return '';
  }
  
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
}